import { FiMinus } from "@react-icons/all-files/fi/FiMinus";
import { FiPlus } from "@react-icons/all-files/fi/FiPlus";
import { useMemo } from "react";
import styled, { css } from "styled-components";

import { useUpdateCubeTicket } from "@core/hooks/mutations/todo";
import useCubeCharacters from "@core/hooks/queries/cube/useCubeCharacters";
import { useIsCompact } from "@core/hooks/usePersistedGridConfig";
import useModalState from "@core/hooks/useModalState";
import { updateCharacterQueryData } from "@core/lib/queryClient";
import type { Character } from "@core/types/character";
import type { Friend } from "@core/types/friend";
import { getCubeTicketKeys } from "@core/utils";

import Button from "@components/Button";
import CubeCharacterManager from "@components/CubeCharacterManager";
import CubeDashboardModal from "@components/CubeDashboardModal";
import Modal from "@components/Modal";

import EditIcon from "@assets/svg/EditIcon";
import MoreDetailIcon from "@assets/svg/MoreDetailIcon";

import CounterActionButton, { CounterValue } from "./CounterActionButton";
import { Wrapper, Counter } from "./ResourceCounter";

interface Props {
  character: Character;
  friend?: Friend;
}

const Cube = ({ character, friend }: Props) => {
  const isCompact = useIsCompact();
  const [cubeDashboardModal, setCubeDashboardModal] =
    useModalState<Character>();
  const [cubeCharacterModal, setCubeCharacterModal] = useModalState<number>();
  const getCubeCharacters = useCubeCharacters();
  const updateCubeTicket = useUpdateCubeTicket({
    onSuccess: (character, { friendUsername }) => {
      updateCharacterQueryData({
        character,
        friendUsername,
      });
    },
  });

  const cubeCharacter = useMemo(() => {
    if (getCubeCharacters.data) {
      return (
        getCubeCharacters.data.find(
          (cubeCharacter) =>
            cubeCharacter.characterName === character.characterName
        ) ?? null
      );
    }

    return null;
  }, [getCubeCharacters]);

  const useCubeCharacter =
    !friend && character.settings.linkCubeCal && !!cubeCharacter;

  const totalCubeTickets = useMemo(() => {
    if (useCubeCharacter && cubeCharacter) {
      return getCubeTicketKeys(cubeCharacter).reduce((acc, key) => {
        return acc + (cubeCharacter[key] as number);
      }, 0);
    }

    return character.cubeTicket;
  }, [character, cubeCharacter]);

  return (
    <Wrapper $compact={isCompact}>
      <Counter $compact={isCompact}>
        {useCubeCharacter ? (
          <>큐브 티켓: {totalCubeTickets} 장</>
        ) : (
          <>
            {!useCubeCharacter && (
              <CounterActionButton
                disabled={character.cubeTicket <= 0}
                onClick={() => {
                  updateCubeTicket.mutate({
                    friendUsername: friend?.friendUsername,
                    characterId: character.characterId,
                    num: -1,
                  });
                }}
              >
                <FiMinus />
              </CounterActionButton>
            )}
            <CounterValue>{totalCubeTickets} 장</CounterValue>
            {!useCubeCharacter && (
              <CounterActionButton
                onClick={() => {
                  updateCubeTicket.mutate({
                    friendUsername: friend?.friendUsername,
                    characterId: character.characterId,
                    num: 1,
                  });
                }}
              >
                <FiPlus />
              </CounterActionButton>
            )}
            <CubeLabel>큐브 티켓</CubeLabel>
          </>
        )}
      </Counter>

      <Buttons $compact={isCompact}>
        {useCubeCharacter && (
          <Button
            css={isCompact ? compactButtonCss : rightButtonCss}
            type="button"
            variant="icon"
            size={isCompact ? 14 : 18}
            onClick={() => setCubeCharacterModal(character.characterId)}
          >
            <EditIcon />
          </Button>
        )}

        <Button
          css={isCompact ? compactButtonCss : rightButtonCss}
          type="button"
          variant="icon"
          size={isCompact ? 14 : 18}
          onClick={() => setCubeDashboardModal(character)}
        >
          <MoreDetailIcon />
        </Button>
      </Buttons>

      <CubeDashboardModal
        onClose={() => setCubeDashboardModal()}
        targetCharacter={cubeDashboardModal}
      />

      <Modal
        isOpen={!!cubeCharacterModal}
        onClose={() => setCubeCharacterModal()}
      >
        <CubeCharacterManager characterId={cubeCharacterModal as number} />
      </Modal>
    </Wrapper>
  );
};

export default Cube;

const Buttons = styled.div<{ $compact?: boolean }>`
  display: flex;
  flex-direction: row;
  flex-shrink: 0;
  ${({ $compact }) => $compact && `gap: 2px;`}
`;

const CubeLabel = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin-left: 3px;
`;

const rightButtonCss = css`
  padding: 8px 6px;
  border-radius: 0;
`;

const compactButtonCss = css`
  padding: 2px 3px;
  border-radius: 0;
`;
